import React, { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import { signIn, signOut } from 'aws-amplify/auth';
import { User, AuthState, UserRole } from '@/types';
import { authService } from '@/services/authService';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  loginWithSSO: (provider: 'AzureAD' | 'ADFS' | 'OKTA') => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  getHostedUIUrl: () => string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    loading: true,
    error: null,
  });

  const checkAuthState = async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      // URLからOAuthコールバックコードをチェック
      const urlParams = new URLSearchParams(window.location.search);
      const authCode = urlParams.get('code');
      const authError = urlParams.get('error');
      
      console.log('🔄 Auth - Checking auth state:', {
        code: authCode ? 'present' : 'none',
        error: authError,
        fullURL: window.location.href,
        search: window.location.search
      });
      
      if (authError) {
        console.error('💥 OAuth Error:', authError);
        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          error: `認証エラー: ${authError}`,
        });
        return;
      }
      
      // OAuth認証コードが存在する場合の処理
      if (authCode) {
        console.log('🔄 Processing OAuth callback code...');
        
        // URLをクリーンアップ
        const newUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        
        // 少し待ってからAmplifyの認証状態を確認
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // authServiceを使用して認証状態を確認
      console.log('🔄 Checking authentication status...');
      const isAuthenticated = await authService.isAuthenticated();
      console.log('🔍 Authentication result:', isAuthenticated);
      
      if (isAuthenticated) {
        console.log('✅ User is authenticated, getting user info...');
        const userInfo = await authService.getCurrentUser();
        console.log('👤 User info:', userInfo);
        
        if (userInfo) {
          const user: User = {
            id: userInfo.id,
            email: userInfo.email,
            name: userInfo.name,
            department: '', // 必要に応じて追加
            role: (userInfo.role as UserRole) || 'user',
            language: 'ja', // デフォルト
            lastLogin: new Date(),
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          setAuthState({
            isAuthenticated: true,
            user,
            loading: false,
            error: null,
          });
          
          console.log('✅ Auth state set to authenticated:', user);
        } else {
          console.log('❌ User info not available, setting unauthenticated');
          setAuthState({
            isAuthenticated: false,
            user: null,
            loading: false,
            error: null,
          });
        }
      } else {
        console.log('❌ User not authenticated');
        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          error: null,
        });
      }
      
    } catch (error: any) {
      console.error('💥 Auth check error:', error);
      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: error.message || '認証状態の確認に失敗しました',
      });
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_MODE === 'true') {
        console.log('Auth - Login attempt:', { email });
      }
      
      // 既存のAmplify認証状態をクリア
      try {
        await signOut({ global: true });
        // トークンキャッシュもクリア
        authService.clearTokenCache();
      } catch (signOutError: any) {
        console.warn('Pre-login signout failed:', signOutError);
        // エラーが発生してもログイン処理を継続
      }
      
      // Amplify signInを使用
      await signIn({ username: email, password });
      
      // 認証成功後、ユーザー情報を再取得
      await checkAuthState();
      
    } catch (error: any) {
      console.error('Login error:', error);
      
      let errorMessage = 'ログインに失敗しました';
      
      // Cognitoエラーメッセージの日本語化
      if (error.name === 'NotAuthorizedException') {
        errorMessage = 'メールアドレスまたはパスワードが正しくありません';
      } else if (error.name === 'UserNotConfirmedException') {
        errorMessage = 'メールアドレスの確認が完了していません';
      } else if (error.name === 'PasswordResetRequiredException') {
        errorMessage = 'パスワードのリセットが必要です';
      } else if (error.name === 'UserNotFoundException') {
        errorMessage = 'ユーザーが見つかりません';
      } else if (error.name === 'TooManyRequestsException') {
        errorMessage = 'リクエストが多すぎます。しばらく待ってから再試行してください';
      } else if (error.name === 'UserAlreadyAuthenticatedException') {
        // 既にログイン済みの場合は、認証状態をチェックして適切に処理
        console.log('User already authenticated, checking auth state...');
        await checkAuthState();
        return; // エラーとして扱わない
      }
      
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      console.log('🔄 Auth - Starting logout with IdP signout flow');
      
      // authServiceのログアウト処理を使用（IdPサインアウトフロー対応）
      await authService.logout();
      
      // 状態をクリア（Cognito Hosted UIにリダイレクトされるため、この後の処理は実行されない可能性がある）
      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
      });
      
    } catch (error: any) {
      console.error('💥 Logout error:', error);
      
      // ログアウトエラーでも状態をクリア
      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
      });
      
      // エラーが発生した場合は緊急ログアウトを実行
      try {
        await authService.emergencyLogout();
      } catch (emergencyError) {
        console.error('💥 Emergency logout also failed:', emergencyError);
        // 最後の手段として強制リダイレクト
        window.location.href = '/login';
      }
    }
  };

  const loginWithSSO = async (provider: 'AzureAD' | 'ADFS' | 'OKTA') => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      console.log('Debug - SSO Login attempt:', { provider });
      
      // 既存のAmplify認証状態をクリア
      try {
        await signOut({ global: true });
        authService.clearTokenCache();
      } catch (signOutError: any) {
        console.warn('Pre-SSO signout failed:', signOutError);
      }
      
      // Hosted UIにリダイレクト
      const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN || 'enterprise-auth-dev-auth';
      const region = import.meta.env.VITE_AWS_REGION || 'ap-northeast-1';
      const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
      
      const params = new URLSearchParams();
      params.append('identity_provider', provider);
      params.append('client_id', clientId);
      params.append('response_type', 'code');
      params.append('scope', 'email openid profile aws.cognito.signin.user.admin');
      params.append('redirect_uri', window.location.origin);
      
      const ssoUrl = `https://${cognitoDomain}.auth.${region}.amazoncognito.com/oauth2/authorize?${params.toString()}`;
      
      console.log('Debug - SSO URL:', ssoUrl);
      window.location.href = ssoUrl;
      
    } catch (error: any) {
      console.error('SSO login error:', error);
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'SSO ログインに失敗しました',
      }));
      throw error;
    }
  };

  const getHostedUIUrl = () => {
    const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN || 'enterprise-auth-dev-auth';
    const region = import.meta.env.VITE_AWS_REGION || 'ap-northeast-1';
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
    
    const params = new URLSearchParams();
    params.append('client_id', clientId);
    params.append('response_type', 'code');
    params.append('scope', 'email openid profile aws.cognito.signin.user.admin');
    params.append('redirect_uri', window.location.origin);
    
    return `https://${cognitoDomain}.auth.${region}.amazoncognito.com/login?${params.toString()}`;
  };

  const refreshUser = async () => {
    await checkAuthState();
  };

  useEffect(() => {
    checkAuthState();
  }, []);

  const contextValue: AuthContextType = {
    ...authState,
    login,
    loginWithSSO,
    logout,
    refreshUser,
    getHostedUIUrl,
  };

  return React.createElement(
    AuthContext.Provider,
    { value: contextValue },
    children
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth;
